import { useMemo, useState, useEffect } from 'react';
import { TreeItem, UseTreeSelectionReturn } from './types';

export const useTreeSelection = <T = any>(initialItems: TreeItem<T>[] = [], singleSelect: boolean = false): UseTreeSelectionReturn<T> => {
  const [items, setItems] = useState<TreeItem<T>[]>(initialItems);

  // Sync internal state with prop changes
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Helper function to clear all selections
  const clearAllSelections = (items: TreeItem<T>[]): TreeItem<T>[] => {
    return items.map((item) => ({
      ...item,
      selected: false,
      subItems: item.subItems ? clearAllSelections(item.subItems) : undefined,
    }));
  };

  // Helper function to set only one item as selected (for single select mode)
  const setOnlyItemSelected = (
    items: TreeItem<T>[],
    targetId: string | number,
    level: 'main' | 'sub' | 'subSub',
    parentId?: string | number,
    grandParentId?: string | number
  ): TreeItem<T>[] => {
    return items.map((item) => {
      if (level === 'main' && item.id === targetId) {
        return {
          ...item,
          selected: true,
          subItems: item.subItems ? clearAllSelections(item.subItems) : undefined,
        };
      } else if (level === 'sub' && item.id === parentId) {
        return {
          ...item,
          selected: false,
          subItems: item.subItems?.map((sub) => ({
            ...sub,
            selected: sub.id === targetId,
            subItems: sub.subItems ? clearAllSelections(sub.subItems) : undefined,
          })),
        };
      } else if (level === 'subSub' && item.id === grandParentId) {
        return {
          ...item,
          selected: false,
          subItems: item.subItems?.map((sub) => ({
            ...sub,
            selected: false,
            subItems: sub.subItems?.map((subSub) => ({
              ...subSub,
              selected: sub.id === parentId && subSub.id === targetId,
            })),
          })),
        };
      } else {
        return {
          ...item,
          selected: false,
          subItems: item.subItems ? clearAllSelections(item.subItems) : undefined,
        };
      }
    });
  };

  // Helper function to get selected values from a tree structure
  const getSelectedValuesFromItems = (items: TreeItem<T>[]): T[] => {
    const selected: T[] = [];

    items.forEach((item) => {
      if (item.selected) {
        selected.push(item.value);
      } else {
        item.subItems?.forEach((sub) => {
          if (sub.selected) {
            selected.push(sub.value);
          } else {
            sub.subItems?.forEach((subSub) => {
              if (subSub.selected) {
                selected.push(subSub.value);
              }
            });
          }
        });
      }
    });

    return selected;
  };

  // Toggle main item and all its sub-items - returns new selected values
  const toggleMainItem = (id: string | number): T[] => {
    let newSelectedValues: T[] = [];

    setItems((prev) => {
      const newItems = singleSelect
        ? setOnlyItemSelected(prev, id, 'main')
        : prev.map((item) => {
            if (item.id === id) {
              const newSelected = !item.selected;
              return {
                ...item,
                selected: newSelected,
                subItems: item.subItems?.map((sub) => ({
                  ...sub,
                  selected: newSelected,
                  subItems: sub.subItems?.map((subSub) => ({
                    ...subSub,
                    selected: newSelected,
                  })),
                })),
              };
            }
            return item;
          });

      newSelectedValues = getSelectedValuesFromItems(newItems);
      return newItems;
    });

    return newSelectedValues;
  };

  // Toggle sub item and update parent selection state - returns new selected values
  const toggleSubItem = (mainId: string | number, subId: string | number): T[] => {
    let newSelectedValues: T[] = [];

    setItems((prev) => {
      const newItems = singleSelect
        ? setOnlyItemSelected(prev, subId, 'sub', mainId)
        : prev.map((item) => {
            if (item.id === mainId) {
              const updatedSubItems = item.subItems?.map((sub) => {
                if (sub.id === subId) {
                  const newSelected = !sub.selected;
                  return {
                    ...sub,
                    selected: newSelected,
                    subItems: sub.subItems?.map((subSub) => ({
                      ...subSub,
                      selected: newSelected,
                    })),
                  };
                }
                return sub;
              });

              // Check if all sub-items are selected
              const allSubSelected = updatedSubItems?.every((sub) => sub.selected) || false;

              return {
                ...item,
                selected: allSubSelected,
                subItems: updatedSubItems,
              };
            }
            return item;
          });

      newSelectedValues = getSelectedValuesFromItems(newItems);
      return newItems;
    });

    return newSelectedValues;
  };

  // Toggle sub-sub item and update parent selection states - returns new selected values
  const toggleSubSubItem = (mainId: string | number, subId: string | number, subSubId: string | number): T[] => {
    let newSelectedValues: T[] = [];

    setItems((prev) => {
      const newItems = singleSelect
        ? setOnlyItemSelected(prev, subSubId, 'subSub', subId, mainId)
        : prev.map((item) => {
            if (item.id === mainId) {
              const updatedSubItems = item.subItems?.map((sub) => {
                if (sub.id === subId) {
                  const subSubToToggle = sub.subItems?.find((ss) => ss.id === subSubId);
                  const newSelected = !subSubToToggle?.selected;

                  const updatedSubSubItems = sub.subItems?.map((subSub) => {
                    if (subSub.id === subSubId) {
                      return { ...subSub, selected: newSelected };
                    }
                    return subSub;
                  });

                  // Check if all sub-sub items are selected
                  const allSubSubSelected = updatedSubSubItems?.every((subSub) => subSub.selected) || false;

                  return {
                    ...sub,
                    selected: allSubSubSelected,
                    subItems: updatedSubSubItems,
                  };
                }
                return sub;
              });

              // Check if all sub-items are selected
              const allSubSelected = updatedSubItems?.every((sub) => sub.selected) || false;

              return {
                ...item,
                selected: allSubSelected,
                subItems: updatedSubItems,
              };
            }
            return item;
          });

      newSelectedValues = getSelectedValuesFromItems(newItems);
      return newItems;
    });

    return newSelectedValues;
  };

  // Select all items
  const selectAll = () => {
    if (singleSelect) return; // Don't allow select all in single select mode

    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        selected: true,
        subItems: item.subItems?.map((sub) => ({
          ...sub,
          selected: true,
          subItems: sub.subItems?.map((subSub) => ({
            ...subSub,
            selected: true,
          })),
        })),
      }))
    );
  };

  // Clear all selections
  const clearAll = () => {
    setItems((prev) => clearAllSelections(prev));
  };

  // Get all selected values (flattened)
  const getSelectedValues = (): T[] => {
    const selected: T[] = [];

    items.forEach((item) => {
      if (item.selected) {
        selected.push(item.value);
      } else {
        item.subItems?.forEach((sub) => {
          if (sub.selected) {
            selected.push(sub.value);
          } else {
            sub.subItems?.forEach((subSub) => {
              if (subSub.selected) {
                selected.push(subSub.value);
              }
            });
          }
        });
      }
    });

    return selected;
  };

  // Check if any item is selected
  const hasSelectedItems = useMemo(() => {
    const hasSelectedMainItem = items.some((item) => item.selected);
    if (hasSelectedMainItem) return true;

    const hasSelectedSubItem = items.some(
      (item) => item.subItems?.some((subItem) => subItem.selected || subItem.subItems?.some((subSubItem) => subSubItem.selected))
    );

    return hasSelectedSubItem;
  }, [items]);

  return {
    items,
    setItems,
    toggleMainItem,
    toggleSubItem,
    toggleSubSubItem,
    selectAll,
    clearAll,
    getSelectedValues,
    hasSelectedItems,
  };
};
